import React from 'react';
import { confirmAlert } from 'react-confirm-alert';
import PropTypes from 'prop-types';
import OutsideClickHandler from 'react-outside-click-handler';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { withRouter } from 'react-router-dom';
import {
  faEllipsisH,
  faChevronLeft,
  faCheck,
  faLevelDown,
  faTrash,
  faEye,
  faEyeSlash,
  faCopy,
  faArrowAltUp,
  faArrowAltDown
} from '@fortawesome/pro-regular-svg-icons';
import { faCabinetFiling } from '@fortawesome/pro-solid-svg-icons';
import cn from 'classnames';
import './ShopCollectionActionsButton.scss';

import { duplicateCollection } from '../../../APIClient/collections';

import { getSectionsForCollections, isShopper } from '../../../Helpers/user_helpers';
import { getGMTTime } from '../../../Helpers/formatting';

import Loader from '../../Loader/Loader';

const ShopCollectionActionsButton = props => {
  const { collection, user } = props;

  const currentCollectionIndex = props.collectionResults.findIndex(c => c.id === collection.id);

  const [showingActionItems, setShowingActionItems] = React.useState(false);
  const [isTakingActionIdx, setIsTakingActionIdx] = React.useState(false);
  const [isMovingSections, setIsMovingSections] = React.useState(null);

  const setActionPanel = (e, show) => {
    e.preventDefault();
    e.stopPropagation();
    setShowingActionItems(show);
  };
  const closeActionPanel = e => setActionPanel(e, false);
  const closeActionPanelWithDelay = e => setTimeout(() => closeActionPanel(e), 25); // To allow outside click to close
  const openActionPanel = e => setActionPanel(e, true);

  const sections = getSectionsForCollections(user);

  // Update in place, effectively local redux since we're not using redux for the collectionResults
  const updateCollectionResult = updates => {
    props.setCollectionResults(props.collectionResults.map(c => (c.id === collection.id ? { ...c, ...updates } : c)));
  };

  // Action Functions
  const deleteCollection = () => {
    const confirmDelete = async () => {
      props.setCollectionResults(props.collectionResults.filter(c => c.id !== collection.id));
      props.deleteCollection(collection).then(({ success }) => {
        if (success) {
          window.ALERT.success('Collection deleted successfully');
          window.__ADD_EVENT__('Shop - Collection Deleted', { title: collection.name });
        } else {
          props.setCollectionResults([
            ...props.collectionResults.slice(0, currentCollectionIndex),
            collection,
            ...props.collectionResults.slice(currentCollectionIndex + 1)
          ]);
          window.__ADD_EVENT__('Shop - Collection Deletion Failure', { title: collection.name, id: collection.id });
        }
      });
    };

    if (collection.total_num_pins > 0) {
      confirmAlert({
        title: 'Just confirming',
        message: 'Are you sure you want to delete this collection?',
        buttons: [
          { label: 'No', className: 'cancel', onClick: () => {} },
          { label: 'Yes', onClick: confirmDelete }
        ]
      });
    } else {
      confirmDelete();
    }
  };

  const toggleVisibility = () => {
    updateCollectionResult({ private: !collection.private });
    props.editCollection(collection.id, { private: !collection.private }).then(({ success, error }) => {
      if (success) {
        window.ALERT.success('Collection hidden successfully');
        window.__ADD_EVENT__(collection.private ? 'Shop - Collection Hidden' : 'Shop - Collection Unhidden', { title: collection.name });
      } else {
        window.ALERT.error(typeof error === 'string' ? error : 'Unknown error, please try again.');
        updateCollectionResult({ private: collection.private });
        window.__ADD_EVENT__(collection.private ? 'Shop - Collection Unhidden Failure' : 'Shop - Collection Hidden Failure', {
          title: collection.name
        });
      }
    });
  };

  const toggleArchive = () => {
    updateCollectionResult({ isArchived: !collection.isArchived });
    props.editCollection(collection.id, { isArchived: !collection.isArchived }).then(({ success, error }) => {
      if (success) {
        props.setCollectionResults(props.collectionResults.filter(c => c.id !== collection.id));
        const section = getSectionsForCollections(props.user).find(s => s.id === collection.Section_id);
        window.ALERT.success(
          collection.isArchived ? `Collection has been moved back to "${section?.title}".` : 'Collection has been moved to your archive.'
        );
        window.__ADD_EVENT__(collection.isArchived ? 'Shop - Collection Unarchived' : 'Shop - Collection Archived', { title: collection.name });
      } else {
        window.ALERT.error(typeof error === 'string' ? error : 'Unknown error, please try again.');
        updateCollectionResult({ isArchived: collection.isArchived });
        window.__ADD_EVENT__(collection.isArchived ? 'Shop - Collection Unarchived Failure' : 'Shop - Collection Archived Failure', {
          title: collection.name
        });
      }
    });
  };

  const isOnTop = props.collectionResults.findIndex(c => c.id === collection.id) === 0;
  const isOnBottom = props.collectionResults.findIndex(c => c.id === collection.id) === props.collectionResults.length - 1;
  const moveToSortOrderRank = sortOrderRank => {
    const isMovingToBottom = sortOrderRank > 0;
    props.setCollectionResults(props.collectionResults.map(c => (c.id === collection.id ? { ...c, sortOrderRank } : c)));
    setShowingActionItems(false); // Close panel since it's moving
    props.editCollection(collection.id, { sortOrderRank }).then(({ success, error }) => {
      if (success) {
        window.ALERT.success(`Collection moved to ${isMovingToBottom ? 'bottom' : 'top'} successfully.`);
      } else {
        window.ALERT.error(typeof error === 'string' ? error : 'Unknown error, please try again.');
        props.setCollectionResults(props.collectionResults);
        window.__ADD_EVENT__(`Shop - Collection Move To ${isMovingToBottom ? 'Bottom' : 'Top'} Failure`, { title: collection.name });
      }
    });
  };
  const moveToTop = () => moveToSortOrderRank(-1 * getGMTTime());
  const moveToBottom = () => moveToSortOrderRank(getGMTTime());

  const duplicate = async () => {
    try {
      const newCollection = await duplicateCollection(collection);
      props.history.push(`/shop/collections/${newCollection.id}`);
    } catch (error) {
      window.ALERT.error(error || `There was an error duplicating that collection, please try again.`);
    }
  };

  const moveToSection = section => {
    props.setCollectionResults(props.collectionResults.filter(c => c.id !== collection.id));
    props.editCollection(collection.id, { Section_id: section.id }).then(({ success, error }) => {
      if (success) {
        window.ALERT.success(`Collection moved to ${section.title} successfully.`);
      } else {
        window.ALERT.error(typeof error === 'string' ? error : 'Unknown error, please try again.');
        props.setCollectionResults([
          ...props.collectionResults.slice(0, currentCollectionIndex),
          collection,
          ...props.collectionResults.slice(currentCollectionIndex + 1)
        ]);
      }
    });
  };

  // Action Buttons
  let actions = isMovingSections
    ? [
        {
          display: 'Back',
          actionFn: () => setIsMovingSections(false),
          icon: faChevronLeft
        },
        ...sections.map(section => {
          const isActive = section.id === collection.Section_id;
          return {
            display: section.title?.toUpperCase(),
            actingDisplay: 'Moving...',
            actionFn: isActive ? null : () => moveToSection(section),
            icon: isActive ? faCheck : faLevelDown
          };
        })
      ]
    : [
        { display: 'Delete', actingDisplay: 'Deleting...', actionFn: deleteCollection, icon: faTrash },
        {
          display: collection.private ? `Show` : `Hide`,
          actingDisplay: collection.private ? 'Showing...' : 'Hiding...',
          actionFn: toggleVisibility,
          icon: collection.private ? faEye : faEyeSlash
        },
        {
          display: 'Change Section',
          actionFn: () => setIsMovingSections(true),
          isHidden: isShopper(user),
          icon: faLevelDown
        },
        {
          display: collection.isArchived ? 'Unarchive' : `Archive`,
          actingDisplay: collection.isArchived ? 'Unarchiving...' : `Archiving...`,
          actionFn: toggleArchive,
          isHidden: isShopper(user),
          icon: faCabinetFiling
        },
        {
          display: 'Duplicate',
          actingDisplay: 'Duplicating...',
          actionFn: duplicate,
          icon: faCopy
        },
        {
          display: 'Move To Top',
          actingDisplay: 'Moving...',
          actionFn: isOnTop ? null : moveToTop,
          icon: faArrowAltUp
        },
        {
          display: 'Move To Bottom',
          actingDisplay: 'Moving...',
          actionFn: isOnBottom ? null : moveToBottom,
          icon: faArrowAltDown
        }
      ];
  actions = actions.filter(action => !action.isHidden);

  return (
    <div className='shop-collection-actions-button-container'>
      <div onClick={showingActionItems ? closeActionPanel : openActionPanel} className='icn show-action-items'>
        <FontAwesomeIcon icon={faEllipsisH} />
        {showingActionItems && (
          <OutsideClickHandler onOutsideClick={closeActionPanelWithDelay}>
            <div className='collection-action-items-container'>
              {actions.map((action, idx) => {
                const { display, actingDisplay, icon, actionFn } = action;
                const isTakingAction = isTakingActionIdx === idx;
                const click = async e => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsTakingActionIdx(idx);
                  await actionFn();
                  setIsTakingActionIdx(null);
                };
                if (!actionFn) return null;
                return (
                  <div onClick={click} key={`collection-action-item-container-${idx}`} className={cn('item', { loading: isTakingAction })}>
                    {isTakingAction ? <Loader size={20} /> : <FontAwesomeIcon icon={icon} />}
                    {isTakingAction ? actingDisplay || display : display}
                  </div>
                );
              })}
            </div>
          </OutsideClickHandler>
        )}
      </div>
    </div>
  );
};

ShopCollectionActionsButton.propTypes = {
  collection: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  editCollection: PropTypes.func.isRequired,
  collectionResults: PropTypes.array.isRequired,
  setCollectionResults: PropTypes.func.isRequired
};

export default withRouter(ShopCollectionActionsButton);
